/**
 * Rastreador de comportamento do checkout (Fase 1 da inteligência de
 * conversão). Fila local + envio em lote por beacon — NUNCA bloqueia nem
 * atrapalha o fluxo de pagamento; falha de rede é silenciosa.
 *
 * Privacidade: nenhum valor digitado sai daqui — só nomes de campo, tipos
 * de evento e códigos curtos. O e-mail entra quando o próprio comprador o
 * fornece (prefill de logado ou digitação) — chave da recuperação de
 * carrinho. Inerte no modo preview do builder.
 */
import { isPreviewMode } from './use-preview-bridge';

type CheckoutEventType =
	| 'view'
	| 'field_focus'
	| 'field_filled'
	| 'field_error'
	| 'method_selected'
	| 'bump_toggled'
	| 'coupon_applied'
	| 'coupon_failed'
	| 'step_advanced'
	| 'submit_attempt'
	| 'payment_failed'
	| 'purchase'
	| 'upsell_shown'
	| 'upsell_accepted'
	| 'upsell_declined'
	| 'downsell_shown'
	| 'downsell_accepted'
	| 'downsell_declined'
	| 'exit_intent_shown'
	| 'exit_intent_email';

let checkoutId: number | null = null;
let sessionId = '';
let email: string | null = null;
let queue: { type: CheckoutEventType; field?: string; meta?: string }[] = [];
let flushTimer = 0;
let endpoint = '';
let apiKey = '';
const seenOnce = new Set<string>();

const newSessionId = () => {
	try {
		// TS antigo não conhece randomUUID — runtime dos navegadores conhece.
		return (crypto as { randomUUID?: () => string }).randomUUID
			? (crypto as unknown as { randomUUID: () => string }).randomUUID()
			: `s_${Date.now().toString(36)}_${Math.random()
					.toString(36)
					.slice(2, 10)}`;
	} catch {
		return `s_${Date.now().toString(36)}_${Math.random()
			.toString(36)
			.slice(2, 10)}`;
	}
};

const flush = () => {
	if (!checkoutId || queue.length === 0 || !endpoint) return;
	const events = queue.splice(0, 25);
	const body = JSON.stringify({ sessionId, email, events });
	try {
		// fetch keepalive sobrevive ao unload (papel do beacon) e manda JSON
		// de verdade; o api_key vai na query — o cpay também o lê de lá.
		const url = `${endpoint}/checkouts/${checkoutId}/events${
			apiKey ? `?api_key=${apiKey}` : ''
		}`;
		void fetch(url, {
			method: 'POST',
			body,
			headers: { 'Content-Type': 'application/json' },
			keepalive: true
		}).catch(() => undefined);
	} catch {
		// Telemetria nunca pode quebrar o checkout.
	}
};

const scheduleFlush = () => {
	if (flushTimer) return;
	flushTimer = window.setTimeout(() => {
		flushTimer = 0;
		flush();
	}, 3000);
};

/** Inicializa a sessão de rastreio de um checkout. */
export const initBehaviorTracker = (input: {
	checkoutId: number;
	payUrl: string;
	apiKey?: string | null;
	/** E-mail do comprador logado (prefill), quando existir. */
	knownEmail?: string | null;
}) => {
	if (isPreviewMode()) return;
	if (checkoutId === input.checkoutId) return;

	checkoutId = input.checkoutId;
	endpoint = input.payUrl.replace(/\/$/, '');
	apiKey = input.apiKey ?? '';
	const storageKey = `checkout-session:${input.checkoutId}`;
	try {
		sessionId =
			sessionStorage.getItem(storageKey) ||
			(() => {
				const fresh = newSessionId();
				sessionStorage.setItem(storageKey, fresh);
				return fresh;
			})();
	} catch {
		sessionId = newSessionId();
	}
	if (input.knownEmail) email = input.knownEmail;

	// meta = URL do checkout — vira o link de retomada ({{checkoutUrl}})
	// nas automações de recuperação de carrinho.
	trackBehavior('view', undefined, window.location.href.slice(0, 255));

	window.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flush();
	});
	window.addEventListener('pagehide', flush);
};

/** E-mail conhecido (digitado/blur) — passa a acompanhar os eventos. */
export const identifyBuyer = (value: string) => {
	if (value && value.includes('@')) email = value.trim().toLowerCase();
};

export const trackBehavior = (
	type: CheckoutEventType,
	field?: string,
	meta?: string,
	options?: { once?: boolean }
) => {
	if (isPreviewMode() || !checkoutId) return;
	if (options?.once) {
		const key = `${type}:${field ?? ''}:${meta ?? ''}`;
		if (seenOnce.has(key)) return;
		seenOnce.add(key);
	}
	queue.push({ type, field, meta });
	if (
		type === 'purchase' ||
		type === 'payment_failed' ||
		type === 'submit_attempt' ||
		// A página de upsell navega logo após — flush imediato.
		type === 'upsell_accepted' ||
		type === 'upsell_declined' ||
		type === 'downsell_accepted' ||
		type === 'downsell_declined'
	) {
		flush();
	} else {
		scheduleFlush();
	}
};
